// src/pages/tags/TagsPage.tsx
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Table,
  Box,
  StatusIndicator,
  TextFilter,
  CollectionPreferences,
  Pagination,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSplitPanel } from '../../context/SplitPanelContext';
import { useTags } from '../../store/tags/useTags';
import { Tag } from 'src/types/helios/Tags';
import TagDetailsForm from 'src/components/tag/TagDetailsForm';
import { ExportButton } from 'src/components/ExportButton';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import useUserProfile from 'src/hooks/useUserProfile';
import {
  getExportTagsConfig,
  getSearchableFields,
  getItemValue,
  initialFilters,
  getFilterTagsProperties,
  getTagsTableDefinitions,
  contentDisplayPreference,
  contentDisplay as defaultContentDisplay,
  pageSizePreference,
  getTagFilterOptions,
} from './tagConfig';
import { useGenericFilter } from 'src/hooks/useGenericFilter';
import FilterModal from 'src/components/filter/FilterModel';
import { PropertyFilterProps } from '@cloudscape-design/components';
import { Persona } from 'src/contstant/Persona';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import { useUsers } from 'src/store/users/useUsers';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { usePropertyFilterPersistence } from 'src/hooks/usePropertyFilterPersistence';
import { useActivePropertyFilterCount } from 'src/hooks/useActivePropertyFilterCount';
import { getPageFilterKey } from 'src/contstant/storageKeys';

const TagsPage = () => {
  const { setContent, setTitle, closeView } = useSplitPanel();
  const [selectedItems, setSelectedItems] = useState<Tag[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [contentDisplay, setContentDisplay] = useState(defaultContentDisplay);

  const { getPersona, getUserProgramId, getUserId, getUserBusinessUnitId, getDivisions } =
    useUserProfile();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): any => {
    return {
      tokens: [],
      operation: 'and' as const,
      searchText: '',
    };
  }, []);

  // Use filter persistence hook
  const { filters: queryFilters, setFilters: setQueryFilters } = useFilterPersistence({
    storageKey: getPageFilterKey(businessUnitId, 'tags'),
    defaultFilters: getDefaultFilters(),
    excludeFields: ['businessUnitId'],
    businessUnitId,
  });

  // Count active filters
  const activeFilterCount = useActivePropertyFilterCount(queryFilters);

  // Table display and pagination state
  const [tablePrefs, setTablePrefs] = useState({
    pageSize: 10,
  });
  const {
    loadTags,
    fetchTagsApiStatus,
    updateTagApiStatus,
    createTagApiStatus,
    deleteTagApiStatus,
    getTagApiStatus,
    tagsList,
    clearAPIStatusMessage,
  } = useTags();
  const { checkUserPermissions, userPermission } = useUsers();
  const { businessUnitList, loadBusinessUnits } = useBusinessUnit();

  const isEditTag = useCallback(() => {
    // Platform Admin can always edit tags
    if (getPersona() === Persona.PLATFORM_ADMIN) {
      return true;
    }

    // For other users, check if they have permission for the currently selected business unit
    // This ensures the permission check is done for the specific business unit context
    if (!userPermission?.permissions) {
      return false;
    }

    return checkFeaturePermission(
      getPersona(),
      permissionConfig.TAGS.EDIT_TAGS,
      userPermission.permissions,
    );
  }, [userPermission?.permissions, getPersona()]);
  const {
    searchText,
    setSearchText: setSearchTextInternal,
    query,
    setQuery,
    filteredItems,
    filterOptions,
  } = useGenericFilter<Tag>(
    tagsList,
    getItemValue,
    getSearchableFields,
    getTagFilterOptions(tagsList),
    undefined, // nestedArrayConfig
    queryFilters.searchText || '', // Use persisted search text from queryFilters
  );

  // Temporary query state for filter modal (only applied on "Apply" click)
  const [tempQuery, setTempQuery] = useState(query);

  // Use custom hook for PropertyFilter persistence (search text + query tokens sync)
  const setSearchText = usePropertyFilterPersistence(
    setSearchTextInternal,
    setQueryFilters,
    queryFilters,
    setQuery,
  );

  // Local pagination for filtered results
  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({
      items: filteredItems || [],
      pageSize: tablePrefs.pageSize,
    });

  useEffect(() => {
    const businessUnitId = getUserBusinessUnitId();
    if (businessUnitId) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: businessUnitId,
        permissions: [permissionConfig.TAGS.EDIT_TAGS],
        divisions: getDivisions(),
      });
      loadBusinessUnits({
        programId: getUserProgramId(),
        isActive: true,
        isDeleted: false,
      });
      setCurrentPageIndex(1);
      loadData();
    }
    return () => {
      closeView();
    };
  }, [getUserBusinessUnitId()]);

  useEffect(() => {
    if (deleteTagApiStatus.isSuccess) {
      loadData();
    }
  }, [deleteTagApiStatus.isSuccess]);

  useEffect(() => {
    if (createTagApiStatus.isSuccess) {
      setCurrentPageIndex(1);
      loadData();
    }
  }, [createTagApiStatus.isSuccess]);

  const loadData = () => {
    loadTags({
      businessUnitId: getUserBusinessUnitId()!,
      programId: getUserProgramId()!,
      isActive: undefined,
    });
  };

  const handleSelectionChange = ({ detail }: { detail: { selectedItems: Tag[] } }) => {
    setSelectedItems(detail.selectedItems);
    openTagDetailsView('Update tag', detail.selectedItems[0]);
  };

  const openTagDetailsView = (title: string, tag?: Tag | null) => {
    clearAPIStatusMessage();
    setTitle(isEditTag() ? title : 'Tag Details');
    setContent(
      <TagDetailsForm
        tag={tag}
        onViewDismiss={() => {
          setSelectedItems([]);
          closeView();
          setCurrentPageIndex(1);
          loadData(); // Refresh list
        }}
        onTagValueAdded={() => {
          loadData(); // Refresh table when tag value is added
        }}
      />,
    );
  };

  return (
    <ContentLayout>
      <FilterModal
        visible={showFilterModal}
        onDismiss={() => {
          setTempQuery(query); // Reset temp query to current query
          setShowFilterModal(false);
        }}
        headerText="Filter tags"
        query={tempQuery}
        onChange={({ detail }: { detail: PropertyFilterProps.Query }) => {
          setTempQuery(detail as any);
        }}
        loading={false}
        filteringProperties={getFilterTagsProperties(
          getPersona() === Persona.PLATFORM_ADMIN,
        )}
        filteringOptions={filterOptions}
        filteringPlaceholder="Filter tags"
        countText={`${filteredItems?.length} matches`}
        onApply={() => {
          setCurrentPageIndex(1);
          setQuery(tempQuery); // Apply temp query to actual query
          setQueryFilters({ ...tempQuery, searchText: queryFilters.searchText });
          setShowFilterModal(false);
        }}
      />
      <ApiStatusFlashBar
        apiStatus={[updateTagApiStatus, createTagApiStatus, deleteTagApiStatus]}
        clearMessages={() => {
          clearAPIStatusMessage();
        }}
      />

      <Box margin={{ top: 'xxxl' }}>
        <Table
          columnDefinitions={getTagsTableDefinitions(businessUnitList).filter(
            (col) => contentDisplay.find((item) => item.id === col.id)?.visible,
          )}
          items={paginatedItems}
          selectedItems={selectedItems}
          selectionType={'single'}
          loading={fetchTagsApiStatus.isLoading}
          loadingText="Loading Tags..."
          header={
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    data-testid="refresh-button"
                    iconName="refresh"
                    onClick={() => {
                      setSelectedItems([]);
                      closeView();
                      setCurrentPageIndex(1);
                      loadData();
                    }}
                  />
                  <ExportButton
                    data={filteredItems || []}
                    config={getExportTagsConfig(businessUnitList)}
                    onError={(error) => {
                      alert(error);
                    }}
                  />
                  {isEditTag() && (
                    <Button
                      variant="normal"
                      data-testid="add-tag-button"
                      onClick={() => openTagDetailsView('Create Tag', null)}
                    >
                      Create Tag
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              Tags
            </Header>
          }
          filter={
            <SpaceBetween size="m" direction="horizontal">
              <TextFilter
                filteringText={searchText}
                filteringPlaceholder="Search Tags"
                filteringAriaLabel="Search Tags"
                onChange={({ detail }) => {
                  setSearchText(detail.filteringText);
                }}
              />
              <Button
                data-testid="filter-button"
                iconName="filter"
                onClick={() => {
                  setTempQuery(query); // Initialize temp query with current query
                  setShowFilterModal(true);
                }}
              >
                {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
              </Button>
            </SpaceBetween>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
          onSelectionChange={handleSelectionChange}
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={{
                pageSize: tablePrefs.pageSize,
                contentDisplay: contentDisplay,
              }}
              contentDisplayPreference={contentDisplayPreference}
              pageSizePreference={pageSizePreference}
              onConfirm={({ detail }) => {
                setTablePrefs((prev) => ({
                  ...prev,
                  pageSize: detail.pageSize || 10,
                }));
                setContentDisplay([...(detail.contentDisplay || defaultContentDisplay)]);
                setCurrentPageIndex(1);
              }}
            />
          }
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No Tags.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </Box>
    </ContentLayout>
  );
};

export default TagsPage;
